import DatabaseVendor from '../types/DatabaseVendor';
import {
    APP_THEME,
    APP_TITLE,
    PIWIK_PRO_ENABLED,
    PIWIK_PRO_ID,
    PIWIK_PRO_URL,
} from './CcxEnv';
import PiwikPro from '@piwikpro/react-piwik-pro';

import Job, {
    STATUS_ERROR,
    TYPE_ADD,
    TYPE_PROMOTE_REPLICA,
    TYPE_REMOVE,
    TYPE_UPGRADE_SERVERS,
    TYPE_VOLUME_SIZE,
    TYPE_WORK,
    TYPE_MODIFYDBCONFIG,
    TYPE_REPAIR_NODE,
    STATUS_FINISHED,
    TYPE_RESTORE_BACKUP,
    JOB_TYPE_CHANGE_VOLUME_TYPE,
    JOB_TYPE_FAILOVER,
    JOB_TYPE_REBOOT_NODE,
} from '../types/Job';
import { DbParameterValue } from '../types/DBparameterDefaults';

interface DbParameterTableInterface {
    currentValue: string;
    defaultValue: string;
    description: string;
    type: string;
    min?: number;
    max?: number;
}

export type SelectFieldOptions = {
    label: string;
    value: string;
};

export class BackendModel {
    constructor(props: Record<string, any>) {
        Object.assign(this, camelCaseObject(props));
    }

    toJSON(): Record<string, any> {
        return snakeCaseObject(this);
    }
}

/**
 * @name camelCase
 * @desc Converts snake_case to camelCase
 * @param {string} inputString
 * @returns {string}
 * @exports
 */
export const camelCase = (inputString: string): string =>
    inputString.split('_').map(camelCaseConvert).join('');

/**
 * @name snakeCase
 * @desc Converts camelCase to snake_case
 * @param {string} inputString
 * @returns {string}
 * @exports
 */
export const snakeCase = (inputString: string): string =>
    inputString.split(snakeCaseRegexp).join('_').toLowerCase();

/**
 * @name camelCaseObject
 * @desc Converts objects keys from snake_case to camelCase and returns new object.
 * @param {Record<string,any>} inputObject
 * @returns {Record<string,any>}
 * @exports
 */
export const camelCaseObject = (
    inputObject: Record<string, any>
): Record<string, any> => objectKeysCase(inputObject, camelCase);

export const snakeCaseObject = (
    inputObject: Record<string, any>
): Record<string, any> => objectKeysCase(inputObject, snakeCase);

const objectKeysCase = (
    inputObject: Record<string, any>,
    convertor: Function
): Record<string, any> =>
    Object.keys(inputObject).reduce((res: Record<string, any>, k: string) => {
        res[convertor(k)] = inputObject[k];
        return res;
    }, {} as Record<string, any>);

const snakeCaseRegexp = /(?=[A-Z])/;

const camelCaseConvert = (s: string, i: number): string =>
    `${i > 0 ? ucFirst(s) : lcFirst(s)}${s.substring(1)}`;

const ucFirst = (s: string): string => s.charAt(0).toUpperCase();

const lcFirst = (s: string): string => s.charAt(0).toLowerCase();

export const getHostnameOrIP = (fqdn: string) => {
    if (fqdn?.includes(':')) {
        const ip = fqdn?.split(':');
        return ip[0];
    } else {
        const splittedFqdn = fqdn?.split('.');
        return splittedFqdn && `${splittedFqdn[0]}`;
    }
};

export const normalizeHostPort = (host: string, port: string) => {
    if (host?.includes(':')) {
        return host;
    } else {
        return `${host}:${port}`;
    }
};

const primaryRole = 'master';
export const sortNodesByRole = (nodes: any[]) => {
    // Separate nodes with the primary role from others
    const primaryNodes = nodes.filter((node: any) =>
        node.role.includes(primaryRole)
    );
    const otherNodes = nodes.filter(
        (node: any) => !node.role.includes(primaryRole)
    );

    // Sort the primary nodes to be at the top
    primaryNodes.sort((a: any, b: any) => a.role.localeCompare(b.role));

    // Combine the sorted primary nodes with the other nodes
    return [...primaryNodes, ...otherNodes];
};

export const getNonErroredJobs = (jobs: Job[]) => {
    return jobs
        ?.filter((job: Job) => {
            return (
                job.status !== STATUS_ERROR && job.status !== STATUS_FINISHED
            );
        })
        ?.filter((job: Job) => {
            return [
                TYPE_ADD,
                TYPE_REMOVE,
                TYPE_VOLUME_SIZE,
                TYPE_PROMOTE_REPLICA,
                TYPE_UPGRADE_SERVERS,
                TYPE_WORK,
                TYPE_MODIFYDBCONFIG,
                TYPE_REPAIR_NODE,
                TYPE_RESTORE_BACKUP,
                JOB_TYPE_CHANGE_VOLUME_TYPE,
                JOB_TYPE_FAILOVER,
                JOB_TYPE_REBOOT_NODE,
            ].includes(job.type);
        });
};

export const isRestoringDatastore = (jobs: Job[]) => {
    const restoringJobs = getNonErroredJobs(jobs);
    if (restoringJobs) {
        const isRestoring = restoringJobs.find(
            (job: Job) => job.type === TYPE_RESTORE_BACKUP
        );
        return !!isRestoring;
    }
    return false;
};

export const isRebootingNode = (jobs: Job[]) => {
    const rebootingJobs = getNonErroredJobs(jobs);
    if (rebootingJobs) {
        const isRebooting = rebootingJobs.find(
            (job: Job) => job.type === JOB_TYPE_REBOOT_NODE
        );
        return !!isRebooting;
    }
    return false;
};

export const isSyncingParameters = (jobs: Job[]) => {
    const syncingJobs = getNonErroredJobs(jobs);
    if (syncingJobs) {
        const isSyncing = syncingJobs.find(
            (job: Job) => job.type === TYPE_MODIFYDBCONFIG
        );
        return !!isSyncing;
    }
    return false;
};

export const singularOrPlural = (count: number, name: string): string => {
    return count === 1 ? name : `${name}s`;
};

export const objectToQueryParams = (obj: any): string => {
    const params = new URLSearchParams();

    Object.entries(obj).forEach(([key, value]: [string, any]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => {
                params.append(key, item);
            });
        } else {
            params.set(key, value.toString());
        }
    });

    return params.toString();
};

export const excludeFakeAddresses = (email: string[] | undefined) => {
    if (!email) return [];

    return email.filter(
        (email) =>
            !email?.includes('@ext.id') && !email?.includes('@lintasarta.extid')
    );
};

export const getDatabaseVendorOptions = (databaseVendors: DatabaseVendor[]) => {
    return databaseVendors
        .filter((vendor: DatabaseVendor) => !vendor.isMssql())
        .map((vendor: DatabaseVendor) => ({
            label: vendor.name,
            value: vendor.code,
        }));
};

export const getDatabaseVersionOptions = (
    databaseVendors: DatabaseVendor[] | any,
    selectedVendor: string | undefined
) => {
    if (selectedVendor) {
        const isInList = databaseVendors.find(
            (vendor: DatabaseVendor) =>
                vendor.code === selectedVendor || vendor.name === selectedVendor
        );
        if (isInList) {
            return isInList.versions
                .sort((a: string, b: string) =>
                    b.localeCompare(a, undefined, { numeric: true })
                )
                .map((version: string) => ({
                    label: version,
                    value: version,
                }));
        }
    }

    return [];
};

export const getDatabaseConfigurationOptions = (
    databaseVendors: DatabaseVendor[] | any,
    selectedVendor: string | undefined
) => {
    if (selectedVendor) {
        const isInList = databaseVendors.find(
            (vendor: DatabaseVendor) =>
                vendor.name === selectedVendor || vendor.code === selectedVendor
        );
        if (isInList) {
            return isInList.types.map((type: any) => {
                return {
                    label: isInList.getVendorTypeName(type.code),
                    value: type.code,
                };
            });
        }
    }
    return [];
};

export const wrapDbParameterValues = (
    recordValues: Record<string, string>,
    defaultParameters: Record<string, DbParameterValue>
): Record<string, DbParameterTableInterface> => {
    const result: Record<string, DbParameterTableInterface> = {};

    for (const [key] of Object.entries(recordValues)) {
        result[key] = {
            currentValue: recordValues[key],
            defaultValue: defaultParameters[key].default_value,
            description: defaultParameters[key].description,
            type: defaultParameters[key].type,
            min: defaultParameters[key].min,
            max: defaultParameters[key].max,
        };
    }

    return result;
};

export const getThemeIllustrationColor = () => {
    const theme: string = APP_THEME;
    switch (theme) {
        case 'ccx':
            return '#3f00ff';
        case 'lintasarta':
            return '#2695D3';
        case 'elastx':
            return '#2f5161';
        default:
            return '#3f00ff';
    }
};

export const getStripeErrorMessage = (error: any): string => {
    if (!error) return 'An unknown error occurred';

    try {
        const errorMessage = error.message || '';
        const jsonStartIndex = errorMessage.lastIndexOf('{');
        const jsonEndIndex = errorMessage.lastIndexOf('}') + 1;

        if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
            const jsonStr = errorMessage.substring(
                jsonStartIndex,
                jsonEndIndex
            );
            const stripeError = JSON.parse(jsonStr);
            return stripeError.message;
        }

        return (
            errorMessage.split('Error:')[1]?.trim() ||
            errorMessage ||
            'Failed to process your card. Please try again.'
        );
    } catch (parseError) {
        const errorMessage = error.message || error.toString();
        return (
            errorMessage.split('Error:')[1]?.trim() ||
            'Failed to process your card. Please try again.'
        );
    }
};

export const isUserDisconnected = (): boolean => {
    return navigator.onLine === false;
};

export const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const setLocalStorage = (
    key: string,
    value: { id: string; type: string; role: string }
) => {
    localStorage.setItem(key, JSON.stringify(value));
};

export const getLocalStorage = (key: string) => {
    return JSON.parse(localStorage.getItem(key) || '{}') ?? {};
};

export const removeLocalStorage = (key: string) => {
    localStorage.removeItem(key);
};

export const isThemeElastx = (): boolean => {
    return APP_THEME === 'elastx';
};

export const isProdEnv = (): boolean => {
    return process.env.NODE_ENV === 'production';
};

export const initializePiwikPro = () => {
    if (PIWIK_PRO_ENABLED) {
        if (PIWIK_PRO_ID && PIWIK_PRO_URL) {
            PiwikPro.initialize(PIWIK_PRO_ID, PIWIK_PRO_URL);
            console.debug('Piwik Pro is enabled and initialized');
        } else {
            console.debug(
                'Piwik Pro is enabled, but missing required variables'
            );
        }
    } else {
        console.debug('Piwik Pro is not enabled');
    }
};

export const isActiveUser = (
    userId: string | undefined,
    recordId: string,
    isAdmin: boolean,
    recordRole: string
) => {
    if (isAdmin && recordRole === 'Admin') {
        return true;
    } else if (isAdmin) {
        return false;
    } else if (userId === recordId) {
        return false;
    } else {
        return true;
    }
};

export const replaceIfRedis = (text: string) => {
    if (text.includes('redis')) {
        return text.replaceAll('redis', 'cache22');
    } else if (text.includes('Redis')) {
        return text.replaceAll('Redis', 'Cache22');
    } else if (text.includes('rediss')) {
        return text.replaceAll('rediss', 'cache22');
    } else if (text.includes('Rediss')) {
        return text.replaceAll('Rediss', 'Cache22');
    } else {
        return text;
    }
};

export const buildWindowTitle = (hostname: string | undefined) => {
    return hostname ? `${APP_TITLE} - ${hostname}` : APP_TITLE;
};
